import React from 'react';
// External imports
import { Redirect, Route, useLocation } from 'react-router-dom';
// Redux imports
import { useSelector } from 'react-redux';
// Internal imports
import { AUTH_STAGE } from '~/app/constants';
import { isRouteAllowed } from '~/app/Router/helper/routesHelper';
import NotFound from '~/app/Pages/NotFound/NotFound.jsx';
import WebHelpers from '~/app/webHelpers.js';

/**
 * Protected Route that is only accessible when a user in logged in or
 * they are going through the two factor authentication process.
 *
 * @category Components
 * @subcategory Auth
 * @param {function} Component - The page needing to be displayed.
 * @returns {function} A component.
 */
const PrivateRoute = ({ render: Component, ...rest }) => {
  const location = useLocation();

  const { authStage, isAuthenticated } = useSelector(state => ({
    authStage: state.app.authStage,
    isAuthenticated: state.app.isAuthenticated,
  }));

  let payload;
  try {
    payload = rest?.requiredRole ? WebHelpers.getJWTPayload() : undefined;
  } catch (_) {
    payload = undefined;
  }

  // Check if route requires permission and user has access
  const hasPermission = rest?.requiresPermission ? isRouteAllowed(rest.path) : true;
  const hasRequiredRole = rest?.requiredRole
    ? payload?.hierarchy_type_id === rest.requiredRole
    : true;

  const routeIncludes = route => {
    return Array.isArray(rest.path) ? rest.path.includes(route) : rest.path === route;
  };

  const isDatabaseRoute = routeIncludes('/database');
  const isTermsRoute = routeIncludes('/terms-and-conditions');
  const isIRSRoute = routeIncludes('/irs-message');
  const isMFAMethodRoute = routeIncludes('/mfa-method');
  const isMFACodeRoute = routeIncludes('/mfa-code');
  const isMFARoute = isMFAMethodRoute || isMFACodeRoute;

  // During SSO finalize, preparers can intentionally land in AWAIT_PICK_OFFICE
  // before becoming authenticated. Permit only the /database route in this state
  // so AppBody can render office selection without opening other private routes.
  const canAccessPickOfficeRoute = authStage === AUTH_STAGE.AWAIT_PICK_OFFICE && isDatabaseRoute;

  const canAccessAuthStageRoute =
    (authStage === AUTH_STAGE.AWAIT_TOC_MESSAGE && isTermsRoute) ||
    (authStage === AUTH_STAGE.AWAIT_IRS_MESSAGE && isIRSRoute) ||
    (authStage === AUTH_STAGE.AWAIT_SECOND_FACTOR && isMFARoute);

  return (
    <Route {...rest}>
      {isAuthenticated || canAccessPickOfficeRoute || canAccessAuthStageRoute ? (
        hasPermission && hasRequiredRole ? (
          <Component />
        ) : (
          <NotFound />
        )
      ) : authStage === AUTH_STAGE.AWAIT_PICK_OFFICE ? (
        <Redirect to={{ pathname: '/database', state: { prevLocation: location } }} />
      ) : authStage === AUTH_STAGE.AWAIT_TOC_MESSAGE ? (
        <Redirect to={{ pathname: '/terms-and-conditions', state: { prevLocation: location } }} />
      ) : authStage === AUTH_STAGE.AWAIT_IRS_MESSAGE ? (
        <Redirect to={{ pathname: '/irs-message', state: { prevLocation: location } }} />
      ) : authStage === AUTH_STAGE.AWAIT_SECOND_FACTOR ? (
        <Redirect to={{ pathname: '/mfa-method', state: { prevLocation: location } }} />
      ) : (
        <Redirect to={{ pathname: '/', state: { prevLocation: location } }} />
      )}
    </Route>
  );
};

export default PrivateRoute;
